/**
 * Admin: per-Service-Group feature overrides.
 *
 * A service group's feature set comes from its serviceProfile (a bundle of
 * defaults in server/config/serviceProfileDefaults.js). An override pins a
 * single feature on or off regardless of that default.
 *
 * Why this screen exists: some profiles disable a feature a given deployment
 * still needs. The clearest case is `hybrid`, which defaults aiOnboarding to
 * FALSE — correct for a therapist-led clinic, wrong for a hybrid pilot that
 * relies on AI-driven intake. Before this screen there was no way to set that
 * from the admin UI, and the failure was silent: onboarding simply did not run.
 *
 * Resolver precedence (server/services/featureResolver.js):
 *   kill switch -> therapist overrides -> THESE overrides -> profile defaults
 *
 * Tri-state per feature:
 *   Inherit  — no override; follow the profile default (sends null, clearing it)
 *   On/Off   — pin the value regardless of profile
 *
 * Backed by PATCH /api/admin/feature-resolver/service-groups/:id, which
 * validates keys against FEATURE_KEYS and audits changes as FEATURE_OVERRIDE_SET.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Button, Alert, CircularProgress, Divider, Chip,
  ToggleButton, ToggleButtonGroup, Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../../../services/api';

// Plain-language descriptions so the screen is usable without reading the code.
const FEATURE_LABELS = {
  aiClientChat: 'AI persona chats with the client',
  aiOnboarding: 'AI-driven onboarding and triage flows',
  aiSuggestedGoals: 'AI proposes goals to the client',
  transcription: 'Live session transcription',
  recording: 'Audio/video retained beyond the transcript',
  draftPostSessionNote: 'AI-drafted clinical note after a session',
  tass: 'TASS therapist clinical cockpit',
  aiSupervisor: 'AI Supervisor (therapist-only reasoning support)',
  journal: 'Client journal',
  structuredCheckins: 'Scheduled structured check-ins',
  asyncMessaging: 'Async secure messaging, client to therapist',
  inSessionAssessmentTrigger: 'Therapist triggers an assessment mid-session',
  privacyPreservingQA: 'QA rubric review without raw-transcript exposure',
  trainingSimulation: 'Therapist private training simulation',
  liveVideo: 'LiveKit-backed live video sessions',
  episodeSummarisation: 'Summarise conversation history into episodes',
  memoryInContext: 'AI sees episode summaries and remembered facts',
  clientMemoryDossier: 'Extract remembered facts about the client',
  memoryRetrieval: 'AI can retrieve archived episodes',
  callContextForAI: 'AI Supporter sees video sessions (notes, summaries, risk). Unset follows the platform setting',
  callTranscriptForAI: 'AI Supporter also sees the latest session transcript. Unset follows the platform setting'
};

export default function ServiceGroupFeatureOverrides({ groupId, token, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [featureKeys, setFeatureKeys] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profile, setProfile] = useState(null);
  const [killSwitchActive, setKillSwitchActive] = useState(false);
  // { [key]: true | false | null }  null = inherit
  const [overrides, setOverrides] = useState({});
  const [initialOverrides, setInitialOverrides] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, resolved] = await Promise.all([
          api.get('/admin/feature-resolver/profiles', token),
          api.get(`/admin/feature-resolver/resolve?serviceGroupId=${groupId}`, token)
        ]);
        if (cancelled) return;

        setFeatureKeys(cat?.data?.featureKeys || []);
        setProfiles(cat?.data?.profiles || []);
        setProfile(resolved?.data?.serviceProfile || null);
        setKillSwitchActive(resolved?.data?.killSwitchActive === true);

        // The resolve endpoint reports the group's own overrides under
        // sources.serviceGroupOverrides. NOTE: when the kill switch is active the
        // resolver deliberately wipes that to {} before returning, so stored
        // overrides are NOT visible in that state — which is why editing is
        // disabled below rather than showing a misleading "Inherit" for everything.
        const existing = resolved?.data?.sources?.serviceGroupOverrides || {};
        const normalised = {};
        (cat?.data?.featureKeys || []).forEach((k) => {
          normalised[k] = Object.prototype.hasOwnProperty.call(existing, k) ? !!existing[k] : null;
        });
        setOverrides(normalised);
        setInitialOverrides(normalised);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Could not load feature configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, token]);

  const profileDefaults = useMemo(() => {
    const p = profiles.find((x) => x.profile === profile);
    return p?.defaults || {};
  }, [profiles, profile]);

  const dirtyKeys = useMemo(
    () => featureKeys.filter((k) => overrides[k] !== initialOverrides[k]),
    [featureKeys, overrides, initialOverrides]
  );

  function setValue(key, value) {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Send only what changed. null clears an override server-side.
      const payload = {};
      dirtyKeys.forEach((k) => { payload[k] = overrides[k]; });
      await api.patch(
        `/admin/feature-resolver/service-groups/${groupId}`,
        { featureOverrides: payload },
        token
      );
      setInitialOverrides({ ...overrides });
      onClose?.({ saved: true, changed: dirtyKeys.length });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {killSwitchActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Editing disabled.</strong> The global Therapist-Led kill switch is active, so every
          group resolves to <strong>ai_forward</strong> and the resolver does not report stored
          overrides. Anything already saved for this group is hidden rather than absent, so editing
          here could overwrite it. Turn the kill switch off to manage overrides.
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Service profile:</Typography>
        <Chip label={profile || 'unknown'} size="small" color="primary" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each feature follows its profile default unless you pin it. Pinned values win over the
        profile but are still subject to per-therapist overrides and the global kill switch.
      </Typography>

      <Divider sx={{ mb: 1 }} />

      <Stack spacing={0.5}>
        {featureKeys.map((key) => {
          const def = profileDefaults[key];
          const value = overrides[key];
          const effective = value === null || value === undefined ? def : value;
          const isDirty = value !== initialOverrides[key];

          return (
            <Box
              key={key}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, py: 1,
                borderLeft: isDirty ? '3px solid' : '3px solid transparent',
                borderColor: isDirty ? 'warning.main' : 'transparent',
                pl: 1, borderRadius: 0.5,
                bgcolor: value !== null && value !== undefined ? 'action.hover' : 'transparent'
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{key}</Typography>
                  <Tooltip title={`Profile default for ${profile}: ${def ? 'on' : 'off'}`}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`default: ${def ? 'on' : 'off'}`}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {FEATURE_LABELS[key] || ''}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color={effective ? 'success.main' : 'text.disabled'} sx={{ minWidth: 64 }}>
                  effective: {effective ? 'on' : 'off'}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  disabled={killSwitchActive}
                  value={value === null || value === undefined ? 'inherit' : (value ? 'on' : 'off')}
                  onChange={(_, v) => {
                    if (v === null) return; // ignore deselect
                    setValue(key, v === 'inherit' ? null : v === 'on');
                  }}
                >
                  <ToggleButton value="inherit">Inherit</ToggleButton>
                  <ToggleButton value="on">On</ToggleButton>
                  <ToggleButton value="off">Off</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" onClick={save} disabled={saving || killSwitchActive || dirtyKeys.length === 0}>
          {saving ? 'Saving…' : `Save${dirtyKeys.length ? ` (${dirtyKeys.length})` : ''}`}
        </Button>
        <Button onClick={() => onClose?.({ saved: false })} disabled={saving}>Cancel</Button>
        {dirtyKeys.length > 0 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <InfoOutlinedIcon fontSize="small" color="warning" />
            <Typography variant="caption" color="text.secondary">
              Unsaved: {dirtyKeys.join(', ')}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
